import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Copy, CheckCircle2, RefreshCw, Globe, ChevronDown, ChevronRight,
  Shield, Loader2, Eye, EyeOff, BookOpen, Terminal, ExternalLink, AlertTriangle,
  BarChart3, Activity, Clock, Webhook, Save, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const REGION_LABELS = { NA: 'North America', UK_EMEA: 'UK & EMEA', APAC: 'Asia Pacific' };

// ─── API Key Management ────────────────────────────────────────────
const ApiKeyManager = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyRegion, setNewKeyRegion] = useState('NA');
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyWebhook, setNewKeyWebhook] = useState('');
  const [newKeyMode, setNewKeyMode] = useState('live');
  const [newKeyRevealed, setNewKeyRevealed] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookInput, setWebhookInput] = useState('');
  const [testingWebhook, setTestingWebhook] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/v1/keys`, { credentials: 'include' });
      if (res.ok) setKeys(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) { toast.error('Enter a key name'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/v1/keys/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ region: newKeyRegion, name: newKeyName.trim(), webhook_url: newKeyWebhook.trim(), mode: newKeyMode }),
      });
      if (!res.ok) { toast.error('Failed to generate key'); return; }
      const data = await res.json();
      setNewKeyRevealed(data.key);
      setNewKeyName('');
      setNewKeyWebhook('');
      toast.success('API key generated! Copy it now — it won\'t be shown again.');
      fetchKeys();
    } catch (e) { toast.error('Error generating key'); }
    finally { setCreating(false); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? Ceipal will lose access.')) return;
    await fetch(`${API}/api/v1/keys/${keyId}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Key revoked');
    fetchKeys();
  };

  const handleSaveWebhook = async (keyId) => {
    const res = await fetch(`${API}/api/v1/keys/${keyId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ webhook_url: webhookInput }),
    });
    if (res.ok) { toast.success('Webhook URL saved'); setEditingWebhook(null); fetchKeys(); }
    else toast.error('Failed to update webhook');
  };

  const handleTestWebhook = async (keyId) => {
    setTestingWebhook(keyId);
    try {
      const res = await fetch(`${API}/api/v1/keys/${keyId}/test-webhook`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.status === 'delivered') {
        toast.success(`Webhook delivered! (HTTP ${data.response_code})`);
      } else if (res.ok) {
        toast.error(`Webhook failed: HTTP ${data.response_code || 'no response'}`);
      } else {
        toast.error(data.detail || 'Test failed');
      }
    } catch (e) { toast.error('Failed to send test'); }
    finally { setTestingWebhook(null); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Integration API Keys</h3>
        <p className="text-sm text-muted-foreground mt-1">Generate region-scoped API keys for Ceipal integration. Each key grants access to jobs in one region only.</p>
      </div>

      {/* Create new key */}
      <div className="bg-card border rounded-xl p-5">
        <h4 className="text-sm font-medium text-foreground mb-3">Generate New Key</h4>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Region</label>
            <select value={newKeyRegion} onChange={(e) => setNewKeyRegion(e.target.value)}
              className="h-9 px-3 rounded-lg border bg-background text-sm text-foreground" data-testid="key-region-select">
              <option value="NA">North America</option>
              <option value="UK_EMEA">UK & EMEA</option>
              <option value="APAC">Asia Pacific</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Mode</label>
            <select value={newKeyMode} onChange={(e) => setNewKeyMode(e.target.value)}
              className="h-9 px-3 rounded-lg border bg-background text-sm text-foreground" data-testid="key-mode-select">
              <option value="live">Live</option>
              <option value="sandbox">Sandbox (mock data)</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground mb-1 block">Key Name</label>
            <input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., Ceipal NA Production"
              className="h-9 w-full px-3 rounded-lg border bg-background text-sm text-foreground" data-testid="key-name-input" />
          </div>
          <button onClick={handleCreate} disabled={creating}
            className="h-9 px-4 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-key-btn">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Generate
          </button>
        </div>
        <div className="mt-3">
          <label className="text-xs text-muted-foreground mb-1 block">Webhook URL (optional — get notified when reports complete)</label>
          <input value={newKeyWebhook} onChange={(e) => setNewKeyWebhook(e.target.value)}
            placeholder="https://your-ceipal-instance.com/webhooks/hirefast"
            className="h-9 w-full px-3 rounded-lg border bg-background text-sm font-mono text-foreground" data-testid="key-webhook-input" />
        </div>

        {/* Revealed key */}
        {newKeyRevealed && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="revealed-key">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-800">Copy this key now — it won't be shown again!</p>
            </div>
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
              <code className="text-xs text-gray-800 flex-1 font-mono break-all">{newKeyRevealed}</code>
              <button onClick={() => copyToClipboard(newKeyRevealed)} className="shrink-0 text-indigo-600 hover:text-indigo-800">
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <button onClick={() => setNewKeyRevealed(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Dismiss</button>
          </div>
        )}
      </div>

      {/* Key list */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No API keys generated yet.</p>
        ) : (
          keys.map(k => (
            <div key={k.key_id} className={`border rounded-xl px-5 py-3 ${k.status === 'active' ? 'bg-card' : 'bg-muted/50 opacity-60'}`}
              data-testid={`key-row-${k.key_id}`}>
              <div className="flex items-center gap-4">
                <Key className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{k.name}</p>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${k.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {k.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{k.key_preview}</p>
                </div>
                <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">{REGION_LABELS[k.region] || k.region}</span>
                {k.mode === 'sandbox' && (
                  <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">SANDBOX</span>
                )}
                {k.mode === 'live' && (
                  <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">LIVE</span>
                )}
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">{k.usage_count || 0} calls</p>
                  <p className="text-[10px] text-muted-foreground">{k.last_used ? `Last: ${new Date(k.last_used).toLocaleDateString()}` : 'Never used'}</p>
                </div>
                {k.status === 'active' && (
                  <button onClick={() => handleRevoke(k.key_id)} className="text-red-400 hover:text-red-600 p-1" title="Revoke">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Webhook URL row */}
              {k.status === 'active' && (
                <div className="mt-2 pt-2 border-t flex items-center gap-2">
                  <Webhook className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  {editingWebhook === k.key_id ? (
                    <>
                      <input value={webhookInput} onChange={(e) => setWebhookInput(e.target.value)}
                        placeholder="https://webhook-url.com/..."
                        className="flex-1 h-7 px-2 rounded border bg-background text-xs font-mono text-foreground" />
                      <button onClick={() => handleSaveWebhook(k.key_id)} className="text-emerald-600 hover:text-emerald-700 p-0.5"><Save className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditingWebhook(null)} className="text-muted-foreground hover:text-foreground p-0.5 text-xs">Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-muted-foreground font-mono flex-1 truncate">{k.webhook_url || 'No webhook configured'}</span>
                      <button onClick={() => { setEditingWebhook(k.key_id); setWebhookInput(k.webhook_url || ''); }}
                        className="text-muted-foreground hover:text-foreground p-0.5"><Edit2 className="h-3 w-3" /></button>
                      {k.webhook_url && (
                        <button onClick={() => handleTestWebhook(k.key_id)} disabled={testingWebhook === k.key_id}
                          className="ml-1 text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 font-medium disabled:opacity-50 flex items-center gap-1"
                          data-testid={`test-webhook-${k.key_id}`}>
                          {testingWebhook === k.key_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
                          Test
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};


// ─── Interactive API Documentation ─────────────────────────────────
const ApiDocumentation = () => {
  const [docs, setDocs] = useState(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [testKey, setTestKey] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/v1/docs`).then(r => r.json()).then(setDocs).catch(console.error);
  }, []);

  const handleTest = async (endpoint) => {
    if (!testKey) { toast.error('Enter your API key first'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const url = `${API}${endpoint.path.replace('{job_id}', 'test')}`;
      const opts = { headers: { 'X-API-Key': testKey } };
      if (endpoint.method === 'POST') {
        opts.method = 'POST';
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(endpoint.request_body ? Object.fromEntries(
          Object.entries(endpoint.request_body).map(([k, v]) => [k, v.split(' (')[0]])
        ) : {});
      }
      const res = await fetch(url, opts);
      const data = await res.json();
      setTestResult({ status: res.status, data });
    } catch (e) {
      setTestResult({ status: 'error', data: { error: e.message } });
    }
    finally { setTesting(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  if (!docs) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-foreground">{docs.api_name} Documentation</h3>
          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">v{docs.version}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Share this documentation with Ceipal for integration.</p>
      </div>

      {/* Auth info */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-indigo-800 mb-2">Authentication</h4>
        <p className="text-xs text-indigo-700">{docs.authentication.description}</p>
        <div className="mt-2 bg-white border border-indigo-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <code className="text-xs font-mono text-gray-800 flex-1">{docs.authentication.example}</code>
          <button onClick={() => copyToClipboard(docs.authentication.example)} className="text-indigo-600"><Copy className="h-3.5 w-3.5" /></button>
        </div>
        <p className="text-xs text-indigo-600 mt-2">Rate Limit: {docs.rate_limiting.limit}</p>
      </div>

      {/* Try It section */}
      <div className="bg-card border rounded-xl p-4">
        <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
          <Terminal className="h-4 w-4" /> Try It — Test API
        </h4>
        <div className="flex gap-2 items-center">
          <input value={testKey} onChange={(e) => setTestKey(e.target.value)}
            placeholder="Paste your API key (hf_...)"
            className="flex-1 h-9 px-3 rounded-lg border bg-background text-sm font-mono text-foreground"
            data-testid="test-api-key-input" />
        </div>
        {testResult && (
          <div className={`mt-3 rounded-lg p-3 text-xs font-mono overflow-x-auto ${testResult.status < 300 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
            <p className="font-semibold mb-1">HTTP {testResult.status}</p>
            <pre className="whitespace-pre-wrap text-gray-700">{JSON.stringify(testResult.data, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Versioning info */}
      {docs.versioning && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-purple-800 mb-2">API Versioning</h4>
          <p className="text-xs text-purple-700 mb-2">{docs.versioning.description}</p>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-purple-600">Current: <code className="font-mono">{docs.versioning.current_version}</code></span>
            <span className="text-xs text-purple-500">Header: <code className="font-mono">{docs.versioning.header}</code></span>
          </div>
          <p className="text-[11px] text-purple-600 italic">{docs.versioning.policy}</p>
        </div>
      )}

      {/* Endpoints */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-foreground">Endpoints</h4>
        {docs.endpoints.map((ep, i) => {
          const expanded = expandedEndpoint === i;
          const methodColor = ep.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';
          return (
            <div key={i} className="border rounded-xl overflow-hidden bg-card" data-testid={`endpoint-${i}`}>
              <button onClick={() => setExpandedEndpoint(expanded ? null : i)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${methodColor} shrink-0`}>{ep.method}</span>
                <code className="text-sm font-mono text-foreground flex-1">{ep.path}</code>
                {ep.auth_required && <Shield className="h-3.5 w-3.5 text-amber-500" title="Auth required" />}
                {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </button>
              {expanded && (
                <div className="border-t px-4 py-3 space-y-3 bg-gray-50/50">
                  <p className="text-sm text-gray-700">{ep.description}</p>

                  {ep.request_body && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Request Body</p>
                      <pre className="bg-white border rounded-lg p-2 text-xs font-mono text-gray-700 overflow-x-auto">
                        {JSON.stringify(ep.request_body, null, 2)}
                      </pre>
                    </div>
                  )}
                  {ep.params && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Query Parameters</p>
                      {Object.entries(ep.params).map(([k, v]) => (
                        <p key={k} className="text-xs text-gray-600"><code className="font-mono text-indigo-600">{k}</code>: {v}</p>
                      ))}
                    </div>
                  )}
                  {ep.response_example && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Response Example</p>
                      <pre className="bg-white border rounded-lg p-2 text-xs font-mono text-gray-700 overflow-x-auto">
                        {JSON.stringify(ep.response_example, null, 2)}
                      </pre>
                    </div>
                  )}
                  {ep.curl && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">cURL</p>
                      <div className="bg-gray-900 rounded-lg p-3 flex items-start gap-2">
                        <code className="text-xs text-green-300 font-mono flex-1 break-all">{ep.curl}</code>
                        <button onClick={() => copyToClipboard(ep.curl)} className="text-gray-400 hover:text-white shrink-0 mt-0.5"><Copy className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  )}
                  {ep.auth_required && (
                    <button onClick={() => handleTest(ep)} disabled={testing}
                      className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 flex items-center gap-1.5">
                      {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Terminal className="h-3 w-3" />}
                      Test Endpoint
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Webhook Events */}
      {docs.webhooks && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Webhook className="h-4 w-4 text-blue-500" /> Webhook Events
          </h4>
          <p className="text-xs text-muted-foreground">{docs.webhooks.description}</p>

          {/* Delivery info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-800 mb-2">Webhook Delivery Details</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
              <p><span className="font-medium">Method:</span> {docs.webhooks.delivery?.method}</p>
              <p><span className="font-medium">Content-Type:</span> {docs.webhooks.delivery?.content_type}</p>
              <p><span className="font-medium">Timeout:</span> {docs.webhooks.delivery?.timeout}</p>
              <p><span className="font-medium">Retries:</span> {docs.webhooks.delivery?.retries}</p>
            </div>
            <p className="text-xs text-blue-600 mt-2 font-medium">Custom Headers:</p>
            {docs.webhooks.delivery?.headers && Object.entries(docs.webhooks.delivery.headers).map(([k, v]) => (
              <p key={k} className="text-xs text-blue-700 ml-2"><code className="font-mono">{k}</code>: {v}</p>
            ))}
          </div>

          {/* Event list */}
          {(docs.webhooks.events || []).map((ev, i) => (
            <div key={i} className="border rounded-xl overflow-hidden bg-card">
              <button onClick={() => setExpandedEndpoint(expandedEndpoint === `wh_${i}` ? null : `wh_${i}`)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700 shrink-0">EVENT</span>
                <code className="text-sm font-mono text-foreground flex-1">{ev.event}</code>
                {expandedEndpoint === `wh_${i}` ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </button>
              {expandedEndpoint === `wh_${i}` && (
                <div className="border-t px-4 py-3 space-y-3 bg-gray-50/50">
                  <p className="text-sm text-gray-700">{ev.description}</p>
                  {ev.next_step && (
                    <p className="text-xs text-indigo-600 italic">Next step: {ev.next_step}</p>
                  )}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Payload Example</p>
                    <pre className="bg-white border rounded-lg p-2 text-xs font-mono text-gray-700 overflow-x-auto">
                      {JSON.stringify(ev.payload_example, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Setup steps */}
          {docs.webhooks.setup_steps && (
            <div className="bg-gray-50 border rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Webhook Setup Steps</p>
              {docs.webhooks.setup_steps.map((step, i) => (
                <p key={i} className="text-xs text-gray-600 leading-relaxed">{step}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Getting Started */}
      {docs.getting_started && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-emerald-500" /> Getting Started
          </h4>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-emerald-800 mb-2">Integration Steps</p>
            {docs.getting_started.steps?.map((step, i) => (
              <p key={i} className="text-xs text-emerald-700 leading-relaxed">{step}</p>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-800 mb-2">Security Notes</p>
            {docs.getting_started.security_notes?.map((note, i) => (
              <p key={i} className="text-xs text-amber-700 leading-relaxed">{note}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const ApiUsageDashboard = () => {
  const [stats, setStats] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, whRes] = await Promise.all([
        fetch(`${API}/api/v1/usage/stats?days=${days}`, { credentials: 'include' }),
        fetch(`${API}/api/v1/webhooks/logs?limit=20`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (whRes.ok) setWebhookLogs(await whRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!stats) return <p className="text-sm text-muted-foreground text-center py-8">No usage data available yet.</p>;

  const maxDaily = Math.max(...(stats.daily || []).map(d => d.calls), 1);
  const maxHourly = Math.max(...(stats.hourly || []).map(h => h.calls), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" /> API Usage Analytics
          </h3>
          <p className="text-sm text-muted-foreground">Track API calls, latency, and webhook deliveries.</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="h-8 px-2 rounded-lg border bg-background text-xs text-foreground">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{stats.total_calls.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total Calls</p>
        </div>
        <div className="bg-card border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{stats.per_key?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Active Keys</p>
        </div>
        <div className="bg-card border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{stats.error_rate}%</p>
          <p className="text-xs text-muted-foreground">Error Rate</p>
        </div>
        <div className="bg-card border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{stats.per_endpoint?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Endpoints Used</p>
        </div>
      </div>

      {/* Daily Volume Chart */}
      {stats.daily?.length > 0 && (
        <div className="bg-card border rounded-xl p-5">
          <h4 className="text-sm font-semibold text-foreground mb-3">Daily API Calls</h4>
          <div className="flex items-end gap-1 h-32">
            {stats.daily.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                <div className="w-full bg-indigo-500 rounded-t transition-all hover:bg-indigo-400"
                  style={{ height: `${(d.calls / maxDaily) * 100}%`, minHeight: d.calls > 0 ? '4px' : '0px' }} />
                <div className="hidden group-hover:block absolute -top-8 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                  {d.date}: {d.calls} calls
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-muted-foreground">{stats.daily[0]?.date}</span>
            <span className="text-[10px] text-muted-foreground">{stats.daily[stats.daily.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Two columns: By Key + By Endpoint */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Per Key */}
        {stats.per_key?.length > 0 && (
          <div className="bg-card border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-3">Usage by API Key</h4>
            <div className="space-y-2">
              {stats.per_key.map((k, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{k.name}</p>
                    <p className="text-[10px] text-muted-foreground">{k.region}</p>
                  </div>
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(k.total / stats.total_calls) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium text-foreground w-12 text-right">{k.total}</span>
                  <span className="text-[10px] text-muted-foreground w-14 text-right">{k.avg_latency_ms}ms</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per Endpoint */}
        {stats.per_endpoint?.length > 0 && (
          <div className="bg-card border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-3">Usage by Endpoint</h4>
            <div className="space-y-2">
              {stats.per_endpoint.map((e, i) => (
                <div key={i} className="flex items-center gap-3">
                  <code className="text-[11px] font-mono text-indigo-600 flex-1 truncate">{e.endpoint}</code>
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(e.calls / stats.total_calls) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium text-foreground w-12 text-right">{e.calls}</span>
                  <span className="text-[10px] text-muted-foreground w-14 text-right">{e.avg_latency_ms}ms</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hourly Distribution */}
      {stats.hourly?.length > 0 && (
        <div className="bg-card border rounded-xl p-5">
          <h4 className="text-sm font-semibold text-foreground mb-3">Hourly Distribution (UTC)</h4>
          <div className="flex items-end gap-0.5 h-20">
            {Array.from({ length: 24 }, (_, h) => {
              const entry = stats.hourly.find(e => e.hour === h);
              const calls = entry ? entry.calls : 0;
              return (
                <div key={h} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-400"
                    style={{ height: `${(calls / maxHourly) * 100}%`, minHeight: calls > 0 ? '2px' : '0px' }} />
                  <div className="hidden group-hover:block absolute -top-7 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded z-10">
                    {h}:00 — {calls}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-muted-foreground">0:00</span>
            <span className="text-[10px] text-muted-foreground">12:00</span>
            <span className="text-[10px] text-muted-foreground">23:00</span>
          </div>
        </div>
      )}

      {/* Webhook Delivery Logs */}
      {webhookLogs.length > 0 && (
        <div className="bg-card border rounded-xl p-5">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Webhook className="h-4 w-4 text-blue-500" /> Recent Webhook Deliveries
          </h4>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {webhookLogs.map((log, i) => (
              <div key={i} className="flex items-center gap-3 text-xs border-b pb-1.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${log.status === 'delivered' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span className="font-medium text-foreground w-40 truncate">{log.event}</span>
                <span className="text-muted-foreground flex-1 truncate font-mono">{log.webhook_url}</span>
                <span className={`font-medium ${log.response_code < 400 ? 'text-emerald-600' : 'text-red-600'}`}>{log.response_code || '—'}</span>
                <span className="text-muted-foreground w-24 text-right">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.total_calls === 0 && (
        <div className="text-center py-12 bg-card border rounded-xl">
          <Activity className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <h3 className="text-base font-medium text-foreground">No API Activity Yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Generate an API key and start making calls to see analytics here.</p>
        </div>
      )}
    </div>
  );
};


// ─── Main Export ────────────────────────────────────────────────────
export { ApiKeyManager, ApiDocumentation, ApiUsageDashboard };
